import type { Topology } from '../types/topology';

export const samplePresets: Record<string, { title: string; desc: string; topology: Topology }> = {
  'sma-sample': {
    title: 'SMA SBS5.0-10',
    desc: '1 inverter, 4 MPPTs, no battery',
    topology: {
      project: {
        address: '222 Exhibition St, Melbourne, VIC, 3000',
        installDate: '2024-09-18',
        installer: { name: 'Solar Proof Solutions', address: '35 Tuckeroo avenue', phone: '1300 000 123' }
      },
      panels: { manufacturer: 'Jinko Solar', model: 'JKMS315PP-72', pmax: 315, voc: 36.89, vmp: 29.86, isc: 11.26, imp: 8.26, fuseRating: 12 },
      inverters: [{
        id: 'inv1', manufacturer: 'SMA', model: 'SBS5.0-10', capacity: 5000, phases: 1, type: 'string',
        mppts: [
          { mpptNumber: 1, maxCurrent: 17, voltageRange: [100, 550], strings: [{ panels: 5 }, { panels: 5 }] },
          { mpptNumber: 2, maxCurrent: 15, voltageRange: [100, 550], strings: [{ panels: 5 }] },
          { mpptNumber: 3, maxCurrent: 15, voltageRange: [100, 550], strings: [{ panels: 5 }] },
          { mpptNumber: 4, maxCurrent: 15, voltageRange: [100, 550], strings: [{ panels: 12 }] }
        ]
      }],
      batteries: [],
      switchgear: { rooftopDcIsolator: true, acIsolator: true, energyMeter: true, serviceFuse: true, ctMetering: false },
      grid: { phases: 1, transformer: true }
    }
  },
  'hybrid-dc': {
    title: 'Hybrid DC-coupled',
    desc: 'Sungrow SH10RT + battery',
    topology: {
      project: {
        address: '12 Residential Close, Sydney, NSW, 2000',
        installDate: '2026-03-15',
        installer: { name: 'Solar Proof Solutions', address: '35 Tuckeroo avenue', phone: '1300 000 123' }
      },
      panels: { manufacturer: 'Longi Solar', model: 'LR5-54HTH-440M', pmax: 440, voc: 41.80, vmp: 34.65, isc: 13.47, imp: 12.70, fuseRating: 20 },
      inverters: [{
        id: 'inv1', manufacturer: 'Sungrow', model: 'SH10RT', capacity: 10000, phases: 1, type: 'hybrid',
        mppts: [
          { mpptNumber: 1, maxCurrent: 15, voltageRange: [150, 950], strings: [{ panels: 10 }] },
          { mpptNumber: 2, maxCurrent: 15, voltageRange: [150, 950], strings: [{ panels: 10 }] }
        ]
      }],
      batteries: [{ id: 'bat1', manufacturer: 'Sungrow', model: 'SBR128', capacity: 12.8, coupling: 'dc', connectedTo: 'inv1' }],
      switchgear: { rooftopDcIsolator: true, acIsolator: true, energyMeter: true, serviceFuse: true, ctMetering: false },
      grid: { phases: 1, transformer: true }
    }
  },
  'ac-coupled': {
    title: 'AC-coupled retrofit',
    desc: 'Fronius + Powerwall 3',
    topology: {
      project: {
        address: '8 Retrofit Rd, Brisbane, QLD, 4000',
        installDate: '2026-04-02',
        installer: { name: 'Solar Proof Solutions', address: '35 Tuckeroo avenue', phone: '1300 000 123' }
      },
      panels: { manufacturer: 'Jinko Solar', model: 'JKM475N-60HL4', pmax: 475, voc: 42.88, vmp: 36.02, isc: 14.11, imp: 13.19, fuseRating: 20 },
      inverters: [{
        id: 'inv1', manufacturer: 'Fronius', model: 'Primo 8.2-1', capacity: 8200, phases: 1, type: 'string',
        mppts: [
          { mpptNumber: 1, maxCurrent: 18, voltageRange: [80, 1000], strings: [{ panels: 10 }] },
          { mpptNumber: 2, maxCurrent: 18, voltageRange: [80, 1000], strings: [{ panels: 10 }] }
        ]
      }],
      batteries: [{
        id: 'bat1', manufacturer: 'Tesla', model: 'Powerwall 3', capacity: 13.5, coupling: 'ac', backupGateway: true,
        inverter: { manufacturer: 'Tesla', model: 'Powerwall 3 Inverter', capacity: 11500, phases: 1 }
      }],
      switchgear: { rooftopDcIsolator: true, acIsolator: true, energyMeter: true, serviceFuse: true, ctMetering: false },
      grid: { phases: 1, transformer: true }
    }
  },
  'commercial-large-10mppt': {
    title: '110 kW industrial — Sungrow SG110CX',
    desc: '1 inverter · 10 MPPTs (collapsed) · 3-phase · CT metering',
    topology: {
      project: {
        address: '7 Distribution Way, Altona North, VIC, 3025',
        installDate: '2026-05-22',
        installer: { name: 'Solar Proof Solutions', address: '35 Tuckeroo avenue', phone: '1300 000 123' }
      },
      panels: {
        manufacturer: 'JA Solar', model: 'JAM72D40-580/MB',
        pmax: 580, voc: 51.85, vmp: 43.68, isc: 14.30, imp: 13.28, fuseRating: 20
      },
      inverters: [{
        id: 'inv1', manufacturer: 'Sungrow', model: 'SG110CX', capacity: 110000, phases: 3, type: 'string',
        mppts: Array.from({ length: 10 }, (_, i) => ({
          mpptNumber: i + 1,
          maxCurrent: 26,
          voltageRange: [200, 1000] as [number, number],
          strings: [{ panels: 11 }, { panels: 11 }]
        }))
      }],
      batteries: [],
      switchgear: { rooftopDcIsolator: true, acIsolator: true, energyMeter: true, serviceFuse: true, ctMetering: true },
      grid: { phases: 3, transformer: true }
    }
  },
  'commercial-100kw': {
    title: '74 kW commercial — 3 × Sungrow SG33CX',
    desc: '3 inverters · 12 strings · 3-phase · CT metering',
    topology: {
      project: {
        address: '24 Industrial Dr, Truganina, VIC, 3029',
        installDate: '2026-05-08',
        installer: { name: 'Solar Proof Solutions', address: '35 Tuckeroo avenue', phone: '1300 000 123' }
      },
      panels: {
        manufacturer: 'JA Solar', model: 'JAM72D40-580/MB',
        pmax: 580, voc: 51.85, vmp: 43.68, isc: 14.30, imp: 13.28, fuseRating: 20
      },
      inverters: [
        {
          id: 'inv1', manufacturer: 'Sungrow', model: 'SG33CX', capacity: 33000, phases: 3, type: 'string',
          mppts: [
            { mpptNumber: 1, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] },
            { mpptNumber: 2, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] },
            { mpptNumber: 3, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] }
          ]
        },
        {
          id: 'inv2', manufacturer: 'Sungrow', model: 'SG33CX', capacity: 33000, phases: 3, type: 'string',
          mppts: [
            { mpptNumber: 1, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] },
            { mpptNumber: 2, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] },
            { mpptNumber: 3, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] }
          ]
        },
        {
          id: 'inv3', manufacturer: 'Sungrow', model: 'SG33CX', capacity: 33000, phases: 3, type: 'string',
          mppts: [
            { mpptNumber: 1, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] },
            { mpptNumber: 2, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] },
            { mpptNumber: 3, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] }
          ]
        }
      ],
      batteries: [],
      switchgear: { rooftopDcIsolator: true, acIsolator: true, energyMeter: true, serviceFuse: true, ctMetering: true },
      grid: { phases: 3, transformer: true }
    }
  },
  'commercial-mixed-2inv': {
    title: '143 kW asymmetric — SG33CX + SG110CX',
    desc: '2 different inverters · 13 strings total · 3-phase · CT metering',
    topology: {
      project: {
        address: '14 Logistics Pl, Dandenong South, VIC, 3175',
        installDate: '2026-06-12',
        installer: { name: 'Solar Proof Solutions', address: '35 Tuckeroo avenue', phone: '1300 000 123' }
      },
      panels: {
        manufacturer: 'JA Solar', model: 'JAM72D40-580/MB',
        pmax: 580, voc: 51.85, vmp: 43.68, isc: 14.30, imp: 13.28, fuseRating: 20
      },
      // Mixed inverter sizes — exercises the multi-inverter routing
      // with two blocks of very different lane-heights (3 vs 10 MPPTs)
      // stacked on top of each other. The IMS column should still align
      // to each inverter's centre even though the blocks have unequal
      // total height.
      inverters: [
        {
          id: 'inv1', manufacturer: 'Sungrow', model: 'SG33CX', capacity: 33000, phases: 3, type: 'string',
          mppts: [
            { mpptNumber: 1, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] },
            { mpptNumber: 2, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] },
            { mpptNumber: 3, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] }
          ]
        },
        {
          id: 'inv2', manufacturer: 'Sungrow', model: 'SG110CX', capacity: 110000, phases: 3, type: 'string',
          mppts: Array.from({ length: 10 }, (_, i) => ({
            mpptNumber: i + 1,
            maxCurrent: 26,
            voltageRange: [200, 1000] as [number, number],
            strings: [{ panels: 11 }, { panels: 11 }]
          }))
        }
      ],
      batteries: [],
      switchgear: { rooftopDcIsolator: true, acIsolator: true, energyMeter: true, serviceFuse: true, ctMetering: true },
      grid: { phases: 3, transformer: true }
    }
  },
  'commercial-5inv-fronius': {
    title: '40 kW commercial — 5 × Fronius Primo 8.2',
    desc: '5 inverters · single-phase x5 · stresses IMS bus convergence',
    topology: {
      project: {
        address: '88 Workshop Way, Sunshine West, VIC, 3020',
        installDate: '2026-06-20',
        installer: { name: 'Solar Proof Solutions', address: '35 Tuckeroo avenue', phone: '1300 000 123' }
      },
      panels: { manufacturer: 'Jinko Solar', model: 'JKM475N-60HL4', pmax: 475, voc: 42.88, vmp: 36.02, isc: 14.11, imp: 13.19, fuseRating: 20 },
      // 5 single-phase string inverters distributed across roof aspects
      // (common on light-commercial sheds with multiple azimuths). Each
      // is wired to its own IMS — the AC bus has to converge 5 wires
      // onto the AC isolator, which should render a clean junction dot
      // and a single visible vertical bus on the left side.
      inverters: Array.from({ length: 5 }, (_, i) => ({
        id: `inv${i + 1}`,
        manufacturer: 'Fronius',
        model: 'Primo 8.2-1',
        capacity: 8200,
        phases: 1 as const,
        type: 'string' as const,
        mppts: [
          { mpptNumber: 1, maxCurrent: 18, voltageRange: [80, 1000] as [number, number], strings: [{ panels: 9 }] },
          { mpptNumber: 2, maxCurrent: 18, voltageRange: [80, 1000] as [number, number], strings: [{ panels: 9 }] }
        ]
      })),
      batteries: [],
      switchgear: { rooftopDcIsolator: true, acIsolator: true, energyMeter: true, serviceFuse: true, ctMetering: false },
      grid: { phases: 1, transformer: true }
    }
  },
  'commercial-mixed-mppt': {
    title: '50 kW commercial — non-uniform MPPTs',
    desc: '1 inverter · mixed string counts per MPPT · tests collapse logic',
    topology: {
      project: {
        address: '3 Mixed Roofs Rd, Wodonga, VIC, 3690',
        installDate: '2026-07-01',
        installer: { name: 'Solar Proof Solutions', address: '35 Tuckeroo avenue', phone: '1300 000 123' }
      },
      panels: {
        manufacturer: 'Trina Solar', model: 'TSM-NEG21C.20',
        pmax: 670, voc: 46.5, vmp: 38.7, isc: 18.36, imp: 17.32, fuseRating: 25
      },
      // Deliberately non-uniform: MPPT 1 is mixed (5 + 7 panels), MPPTs
      // 2 + 3 are uniform 2-string each, MPPTs 4 + 5 are uniform 1-string
      // each. Exercises every branch of the lane-collapse decision tree:
      //   - MPPT 1 → mixed; renders 2 individual lanes labelled "String X
      //     of 2 · MPPT 1"
      //   - MPPTs 2 + 3 → uniform-but-different signature collapses to 1
      //     lane each: "MPPT 2 · × 2 strings", "MPPT 3 · × 2 strings"
      //   - MPPTs 4 + 5 → consecutive uniform with matching signature
      //     collapses into a single lane: "MPPTs 4–5"
      inverters: [{
        id: 'inv1', manufacturer: 'Sungrow', model: 'SG50CX', capacity: 50000, phases: 3, type: 'string',
        mppts: [
          { mpptNumber: 1, maxCurrent: 30, voltageRange: [200, 1000], strings: [{ panels: 5 }, { panels: 7 }] },
          { mpptNumber: 2, maxCurrent: 30, voltageRange: [200, 1000], strings: [{ panels: 12 }, { panels: 12 }] },
          { mpptNumber: 3, maxCurrent: 30, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] },
          { mpptNumber: 4, maxCurrent: 30, voltageRange: [200, 1000], strings: [{ panels: 16 }] },
          { mpptNumber: 5, maxCurrent: 30, voltageRange: [200, 1000], strings: [{ panels: 16 }] }
        ]
      }],
      batteries: [],
      switchgear: { rooftopDcIsolator: true, acIsolator: true, energyMeter: true, serviceFuse: true, ctMetering: true },
      grid: { phases: 3, transformer: true }
    }
  },
  'commercial-utility-500kw': {
    title: '550 kW utility-scale — 5 × SG110CX',
    desc: '5 inverters · 50 MPPTs total (collapsed to 1 lane each) · 3-phase',
    topology: {
      project: {
        address: '120 Solar Farm Rd, Wemen, VIC, 3549',
        installDate: '2026-08-14',
        installer: { name: 'Solar Proof Solutions', address: '35 Tuckeroo avenue', phone: '1300 000 123' }
      },
      panels: {
        manufacturer: 'JA Solar', model: 'JAM72D40-580/MB',
        pmax: 580, voc: 51.85, vmp: 43.68, isc: 14.30, imp: 13.28, fuseRating: 20
      },
      // 5 SG110CX = ~550 kW DC, ~500 kW AC after clipping. Ground-mount
      // ag/utility scale. With MPPT collapse each inverter shows as ONE
      // lane labelled "MPPTs 1–10 · × 2 strings each" — so the whole
      // 1100-panel array still fits on an A4 page. Stresses the
      // 5-inverter vertical stack height + AC bus convergence at scale.
      inverters: Array.from({ length: 5 }, (_, n) => ({
        id: `inv${n + 1}`,
        manufacturer: 'Sungrow',
        model: 'SG110CX',
        capacity: 110000,
        phases: 3 as const,
        type: 'string' as const,
        mppts: Array.from({ length: 10 }, (_, i) => ({
          mpptNumber: i + 1,
          maxCurrent: 26,
          voltageRange: [200, 1000] as [number, number],
          strings: [{ panels: 11 }, { panels: 11 }]
        }))
      })),
      batteries: [],
      switchgear: { rooftopDcIsolator: true, acIsolator: true, energyMeter: true, serviceFuse: true, ctMetering: true },
      grid: { phases: 3, transformer: true }
    }
  },
  'commercial-hybrid-plus-string': {
    title: '80 kW hybrid + storage — SH25T + SG33CX',
    desc: '2 inverters (1 hybrid + 1 string) · DC battery on hybrid · CT metering',
    topology: {
      project: {
        address: '17 Battery Lane, Newcastle, NSW, 2300',
        installDate: '2026-09-04',
        installer: { name: 'Solar Proof Solutions', address: '35 Tuckeroo avenue', phone: '1300 000 123' }
      },
      panels: {
        manufacturer: 'JA Solar', model: 'JAM72D40-580/MB',
        pmax: 580, voc: 51.85, vmp: 43.68, isc: 14.30, imp: 13.28, fuseRating: 20
      },
      // Hybrid SH25T (battery storage block) + a separate SG33CX for
      // the additional PV. Tests multi-inverter routing where one
      // block has a battery hanging off it (DC-coupled) and the other
      // is plain string. Both should get IMSes on the left, both
      // should converge on the AC bus.
      inverters: [
        {
          id: 'inv1', manufacturer: 'Sungrow', model: 'SH25T', capacity: 25000, phases: 3, type: 'hybrid',
          mppts: [
            { mpptNumber: 1, maxCurrent: 30, voltageRange: [200, 1000], strings: [{ panels: 12 }, { panels: 12 }] },
            { mpptNumber: 2, maxCurrent: 30, voltageRange: [200, 1000], strings: [{ panels: 12 }, { panels: 12 }] },
            { mpptNumber: 3, maxCurrent: 30, voltageRange: [200, 1000], strings: [{ panels: 12 }, { panels: 12 }] }
          ]
        },
        {
          id: 'inv2', manufacturer: 'Sungrow', model: 'SG33CX', capacity: 33000, phases: 3, type: 'string',
          mppts: [
            { mpptNumber: 1, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] },
            { mpptNumber: 2, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] },
            { mpptNumber: 3, maxCurrent: 26, voltageRange: [200, 1000], strings: [{ panels: 14 }, { panels: 14 }] }
          ]
        }
      ],
      batteries: [
        { id: 'bat1', manufacturer: 'Sungrow', model: 'SBR256', capacity: 25.6, coupling: 'dc', connectedTo: 'inv1' }
      ],
      switchgear: { rooftopDcIsolator: true, acIsolator: true, energyMeter: true, serviceFuse: true, ctMetering: true },
      grid: { phases: 3, transformer: true }
    }
  }
};